import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Users, FileText, Sliders, Clock, ShieldCheck, Send,
  ArrowLeft, ArrowRight, AlertTriangle, CheckCircle, XCircle, Info,
  Paperclip, X, Loader2, Images,
} from 'lucide-react';
import { MediaLibraryPicker } from '@/components/MediaLibraryPicker';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Modal } from '@/components/Modal';
import {
  whatsappBroadcastsService,
  CreateBroadcastInput,
  ManualPhone,
  MessageMode,
  PreflightResult,
} from '@/services/whatsapp-broadcasts.service';
import { TemplateVariablesPanel, extractTemplateVariables, defaultMappingFor } from '@/components/templates/TemplateVariablesPanel';
import { TemplatePreview } from '@/components/templates/TemplatePreview';
import { MediaUpload } from '@/components/MediaUpload';
import { templatesService, Template, HeaderOverride } from '@/services/templates.service';
import { contactsService, Contact } from '@/services/contacts.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';
import { uploadsService } from '@/services/uploads.service';

type Step = 'audience' | 'template' | 'variables' | 'schedule' | 'review' | 'send';
const STEPS: { key: Step; label: string; icon: React.ReactNode }[] = [
  { key: 'audience',  label: 'Audience',  icon: <Users size={14} /> },
  { key: 'template',  label: 'Template',  icon: <FileText size={14} /> },
  { key: 'variables', label: 'Variables', icon: <Sliders size={14} /> },
  { key: 'schedule',  label: 'Schedule',  icon: <Clock size={14} /> },
  { key: 'review',    label: 'Review',    icon: <ShieldCheck size={14} /> },
  { key: 'send',      label: 'Send',      icon: <Send size={14} /> },
];

const TIER_LIMIT: Record<string, number> = {
  TIER_50: 50, TIER_250: 250, TIER_1K: 1000, TIER_10K: 10000, TIER_100K: 100000,
};

export default function BroadcastComposer() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('audience');
  const [name, setName] = useState('');

  // Account
  const [accounts, setAccounts] = useState<WhatsappAccount[]>([]);
  const [accountUid, setAccountUid] = useState('');

  // Audience
  const [audienceMode, setAudienceMode] = useState<'manual' | 'contacts' | 'groups'>('manual');
  const [pasteText, setPasteText] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pickedContactUids, setPickedContactUids] = useState<Set<string>>(new Set());
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [pickedGroupUids, setPickedGroupUids] = useState<Set<string>>(new Set());

  // Message mode + content
  const [messageMode, setMessageMode] = useState<MessageMode>('template');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [pickedTemplateUid, setPickedTemplateUid] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  // Optional media on the custom message — uploaded to ImageKit on pick,
  // sent with customMessage as the caption.
  const [customMedia, setCustomMedia] = useState<{
    url: string;
    type: 'image' | 'video' | 'document';
    filename?: string;
  } | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const pickedTemplate = useMemo(
    () => templates.find((t) => t.templateUid === pickedTemplateUid) ?? null,
    [templates, pickedTemplateUid],
  );

  // Variables
  const [variableOverrides, setVariableOverrides] = useState<Record<string, string>>({});
  const [variableMappings, setVariableMappings] = useState<Record<string, string>>({});
  const [headerOverride, setHeaderOverride] = useState<HeaderOverride>({});
  const [previewContactUid, setPreviewContactUid] = useState('');
  useEffect(() => {
    setVariableOverrides({});
    setHeaderOverride({});
    setVariableMappings(Object.fromEntries((pickedTemplate ? extractTemplateVariables(pickedTemplate) : []).map(name => [name, defaultMappingFor(name)])));
  }, [pickedTemplate]);

  // Schedule
  const [scheduleMode, setScheduleMode] = useState<'send_now' | 'scheduled'>('send_now');
  const [scheduledAt, setScheduledAt] = useState('');

  // Persisted draft + preflight
  const [persistedUid, setPersistedUid] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [running, setRunning] = useState(false);

  // Send
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bootstrap WA accounts + groups + templates
  useEffect(() => {
    whatsappAccountService.list().then((rows) => {
      setAccounts(rows);
      const def = rows.find((a) => a.isDefault) ?? rows[0];
      if (def) setAccountUid(def.accountUid);
    }).catch(() => {});
    contactGroupsService.list().then(setGroups).catch(() => {});
  }, []);

  // Re-fetch templates whenever the selected account changes so the picker
  // only shows templates approved for THAT WABA — broadcasting an unrelated
  // account's template would be rejected by Meta at send time.
  useEffect(() => {
    setPickedTemplateUid('');
    if (!accountUid) { setTemplates([]); return; }
    templatesService.list({ status: 'APPROVED', limit: 200, accountUid })
      .then((r) => setTemplates(r.items)).catch(() => {});
  }, [accountUid]);

  // Lazy-load contacts only when entering the contacts tab
  useEffect(() => {
    if (audienceMode === 'contacts' && contacts.length === 0) {
      contactsService.list({ limit: 200 }).then((r) => setContacts(r.items)).catch(() => {});
    }
  }, [audienceMode, contacts.length]);

  // Audience size estimate
  const audienceCount = useMemo(() => {
    if (audienceMode === 'manual') {
      return pasteText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).length;
    }
    if (audienceMode === 'contacts') return pickedContactUids.size;
    if (audienceMode === 'groups') {
      return groups.filter((g) => pickedGroupUids.has(g.groupUid)).reduce((sum, g) => sum + (g.contactCount ?? 0), 0);
    }
    return 0;
  }, [audienceMode, pasteText, pickedContactUids, pickedGroupUids, groups]);

  const isStepValid = (s: Step): boolean => {
    if (s === 'audience') return !!name && !!accountUid && audienceCount > 0;
    if (s === 'template') {
      return messageMode === 'template'
        ? !!pickedTemplateUid
        : customMessage.trim().length > 0 || !!customMedia;
    }
    if (s === 'variables') return true; // optional
    if (s === 'schedule') return scheduleMode === 'send_now' || (scheduleMode === 'scheduled' && !!scheduledAt);
    if (s === 'review') return !!preflight && preflight.verdict !== 'BLOCK';
    return true;
  };

  const buildPayload = (): CreateBroadcastInput => {
    const manualPhones: ManualPhone[] | undefined =
      audienceMode === 'manual'
        ? pasteText
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
            .map((phone) => ({ phone, defaultCountry: 'AE' }))
        : undefined;
    return {
      name,
      whatsappAccountUid: accountUid,
      messageMode,
      templateUid: messageMode === 'template' ? pickedTemplateUid : undefined,
      templateLanguage: messageMode === 'template' ? (pickedTemplate?.language ?? 'en') : undefined,
      customMessage: messageMode === 'custom' ? customMessage.trim() : undefined,
      customMediaUrl: messageMode === 'custom' ? customMedia?.url : undefined,
      customMediaType: messageMode === 'custom' ? customMedia?.type : undefined,
      customMediaFilename: messageMode === 'custom' ? customMedia?.filename : undefined,
      variableOverrides,
      variableMappings,
      headerOverride: messageMode === 'template' ? headerOverride : undefined,
      scheduleMode,
      scheduledAt: scheduleMode === 'scheduled' ? new Date(scheduledAt).toISOString() : undefined,
      audienceContactUids: audienceMode === 'contacts' ? Array.from(pickedContactUids) : undefined,
      audienceGroupUids: audienceMode === 'groups' ? Array.from(pickedGroupUids) : undefined,
      audienceManualPhones: manualPhones,
    };
  };

  const handleNext = async () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (!isStepValid(step)) {
      toast.error('Complete this step first');
      return;
    }
    const next = STEPS[idx + 1]?.key;
    if (!next) return;

    // Entering review → persist draft + run preflight
    if (next === 'review' && !persistedUid) {
      setRunning(true);
      try {
        const created = await whatsappBroadcastsService.create(buildPayload());
        setPersistedUid(created.broadcastUid);
        const pf = await whatsappBroadcastsService.preflight(created.broadcastUid);
        setPreflight(pf);
        setStep('review');
      } catch {
        // interceptor surfaces
        return;
      } finally {
        setRunning(false);
      }
    } else {
      setStep(next);
    }
  };

  const handleBack = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    const prev = STEPS[idx - 1]?.key;
    if (prev) {
      if (step === 'review') { setPersistedUid(null); setPreflight(null); }
      setStep(prev);
    }
  };

  const handleStart = async () => {
    if (!persistedUid) return;
    setSubmitting(true);
    try {
      await whatsappBroadcastsService.start(persistedUid);
      toast.success('Broadcast started');
      nav(`/whatsapp-account/bulk/${persistedUid}`);
    } catch {
      // interceptor surfaces
    } finally {
      setSubmitting(false);
      setShowSendConfirm(false);
    }
  };

  return (
    <div>
      <WhatsAppHeader section="campaigns"
        title="New WhatsApp broadcast"
        description="Build, preflight, and dispatch a bulk send."
      />

      <div className="px-7 pt-5 pb-24 space-y-5">
        {/* Step indicator */}
        <Card padded>
          <ol className="flex flex-wrap items-center gap-1 text-xs">
            {STEPS.map((s, i) => {
              const idx = STEPS.findIndex((x) => x.key === step);
              const passed = i < idx;
              const active = i === idx;
              return (
                <li key={s.key} className="flex items-center gap-1">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium ${
                      active ? 'bg-primary-soft text-primary'
                      : passed ? 'bg-success-soft text-success'
                      : 'bg-surface-alt text-text-muted'
                    }`}
                  >
                    {s.icon} {s.label}
                  </span>
                  {i < STEPS.length - 1 && <ArrowRight size={11} className="text-text-muted" />}
                </li>
              );
            })}
          </ol>
        </Card>

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        {/* Step content */}
        <Card padded>
          {step === 'audience' && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Broadcast name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. April promo — UAE" />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Send from</label>
                <Select value={accountUid} onChange={(e) => setAccountUid(e.target.value)} disabled={accounts.length === 0}>
                  {accounts.length === 0 && <option value="">No WhatsApp accounts</option>}
                  {accounts.map((a) => (
                    <option key={a.accountUid} value={a.accountUid}>
                      {a.businessName} · {a.displayPhoneNumber}{a.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-secondary">Audience source</label>
                <div className="flex gap-2">
                  {([
                    { v: 'manual',   l: 'Paste numbers' },
                    { v: 'contacts', l: 'Pick contacts' },
                    { v: 'groups',   l: 'Pick groups' },
                  ] as const).map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      onClick={() => setAudienceMode(m.v)}
                      className={`rounded-lg border px-4 py-2 text-xs font-medium transition-colors ${
                        audienceMode === m.v
                          ? 'border-primary bg-primary-soft text-primary'
                          : 'border-border text-text-muted hover:border-border-strong'
                      }`}
                    >
                      {m.l}
                    </button>
                  ))}
                </div>
              </div>

              {audienceMode === 'manual' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">
                    One phone per line (E.164 preferred — invalids are stripped server-side)
                  </label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={8}
                    placeholder="+971501234567&#10;+971502345678&#10;…"
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft"
                  />
                </div>
              )}

              {audienceMode === 'contacts' && (
                <div>
                  <p className="mb-2 text-xs text-text-muted">Showing first 200 contacts. Use Groups for larger audiences.</p>
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                    {contacts.map((c) => (
                      <label key={c.contactUid} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-alt cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={pickedContactUids.has(c.contactUid)}
                          onChange={() => {
                            const next = new Set(pickedContactUids);
                            if (next.has(c.contactUid)) next.delete(c.contactUid);
                            else next.add(c.contactUid);
                            setPickedContactUids(next);
                          }}
                        />
                        <span className="flex-1 text-xs text-text-primary">{c.firstName} {c.lastName ?? ''}</span>
                        <span className="font-mono text-2xs text-text-muted">{c.phone}</span>
                      </label>
                    ))}
                    {contacts.length === 0 && <p className="py-4 text-center text-xs text-text-muted">No contacts yet.</p>}
                  </div>
                </div>
              )}

              {audienceMode === 'groups' && (
                <div>
                  <div className="space-y-1 rounded-lg border border-border p-2">
                    {groups.map((g) => (
                      <label key={g.groupUid} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface-alt cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={pickedGroupUids.has(g.groupUid)}
                          onChange={() => {
                            const next = new Set(pickedGroupUids);
                            if (next.has(g.groupUid)) next.delete(g.groupUid);
                            else next.add(g.groupUid);
                            setPickedGroupUids(next);
                          }}
                        />
                        <span className="flex-1 text-xs text-text-primary">{g.name}</span>
                        <span className="text-2xs text-text-muted">{g.contactCount.toLocaleString()} contacts</span>
                      </label>
                    ))}
                    {groups.length === 0 && <p className="py-4 text-center text-xs text-text-muted">No groups defined.</p>}
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-info-soft px-3 py-2 text-xs text-info">
                <strong>{audienceCount.toLocaleString()}</strong> recipient{audienceCount === 1 ? '' : 's'} selected.
                The system will dedupe, strip opt-outs, and validate format on submit.
              </div>
            </div>
          )}

          {step === 'template' && (
            <div className="space-y-4">
              {/* Mode toggle */}
              <div className="flex gap-2">
                {([
                  { v: 'template', l: 'Approved template', sub: 'Works any time, any recipient' },
                  { v: 'custom',   l: 'Custom message',    sub: 'Free-form text — 24h window only' },
                ] as const).map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setMessageMode(m.v)}
                    className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                      messageMode === m.v
                        ? 'border-primary bg-primary-soft'
                        : 'border-border hover:bg-surface-alt'
                    }`}
                  >
                    <div className="text-sm font-semibold text-text-primary">{m.l}</div>
                    <div className="mt-0.5 text-2xs text-text-muted">{m.sub}</div>
                  </button>
                ))}
              </div>

              {messageMode === 'template' && (
                <>
                  <p className="text-xs text-text-muted">
                    Only approved templates are listed. Templates currently paused by Meta are disabled.
                  </p>
                  {templates.length === 0 ? (
                    <p className="py-4 text-center text-xs text-text-muted">No approved templates yet.</p>
                  ) : (
                    <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border border-border bg-surface-alt/30 p-2">
                      {templates.map((t) => {
                        const selected = pickedTemplateUid === t.templateUid;
                        return (
                          <button
                            key={t.templateUid}
                            type="button"
                            onClick={() => setPickedTemplateUid(t.templateUid)}
                            className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                              selected
                                ? 'border-primary bg-primary-soft'
                                : 'border-border bg-surface hover:bg-surface-alt'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-text-primary">{t.name}</span>
                                <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{t.language}</span>
                                <span className="rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info capitalize">{t.category}</span>
                              </div>
                              <p className="mt-1 line-clamp-2 text-xs text-text-secondary">{t.body}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {messageMode === 'custom' && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-xs text-text-secondary">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
                    <div>
                      <strong className="text-text-primary">Custom messages have a hard limit.</strong>{' '}
                      WhatsApp only delivers free-form text to recipients within their 24-hour
                      customer-service window (i.e. they messaged you in the last 24h). Recipients
                      outside the window will fail with errorCode 131047. For cold outreach, use an
                      approved template instead.
                    </div>
                  </div>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={6}
                    maxLength={4096}
                    placeholder="Write your message. You can use {{firstName}}, {{custom}} placeholders — these substitute from per-recipient variables (or the defaults you set on the next step)."
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft"
                  />
                  <div className="text-right text-2xs text-text-muted tabular-nums">
                    {customMessage.length} / 4096
                  </div>

                  {/* Optional media attachment — message text becomes the caption */}
                  {customMedia ? (
                    <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-alt px-3 py-2">
                      {customMedia.type === 'image' ? (
                        <img src={customMedia.url} alt="" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded bg-surface text-text-muted">
                          <Paperclip size={16} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-text-primary">
                          {customMedia.filename ?? `${customMedia.type} attachment`}
                        </div>
                        <div className="text-2xs text-text-muted">
                          Sent {customMessage.trim() ? 'with your message as the caption' : 'without a caption'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCustomMedia(null)}
                        className="rounded p-1 text-text-muted hover:bg-surface hover:text-danger"
                        title="Remove attachment"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text-secondary hover:bg-surface-alt">
                      {uploadingMedia ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
                      {uploadingMedia ? 'Uploading…' : 'Attach image / video / document (optional)'}
                      <input
                        type="file"
                        accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        disabled={uploadingMedia}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          const mime = file.type || 'application/octet-stream';
                          const type: 'image' | 'video' | 'document' =
                            mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : 'document';
                          // Meta size caps: image 5MB, video 16MB, document 100MB
                          const cap = type === 'image' ? 5 : type === 'video' ? 16 : 100;
                          if (file.size > cap * 1024 * 1024) {
                            toast.error(`${type} too large (max ${cap}MB)`);
                            return;
                          }
                          setUploadingMedia(true);
                          try {
                            const r = await uploadsService.imagekit(file, `whatsapp/campaign/${type === 'image' ? 'images' : type === 'video' ? 'videos' : 'documents'}`, `whatsapp,broadcast,${type}`);
                            setCustomMedia({ url: r.url, type, filename: type === 'document' ? file.name : undefined });
                          } catch (err: any) {
                            // axios interceptor already toasted backend message
                          } finally {
                            setUploadingMedia(false);
                          }
                        }}
                      />
                    </label>
                  )}
                  {!customMedia && (
                    <button
                      type="button"
                      onClick={() => setLibraryOpen(true)}
                      className="ml-2 inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text-secondary hover:bg-surface-alt"
                    >
                      <Images size={13} /> Choose from library
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'variables' && (
            <div className="space-y-4">
              {messageMode === 'template' && !pickedTemplate && (
                <p className="text-xs text-text-muted">Pick a template first.</p>
              )}

              {messageMode === 'template' && pickedTemplate && (
                <>
                  <div className="rounded-lg border border-border bg-surface-alt/40 p-3">
                    <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">Template body</div>
                    <pre className="whitespace-pre-wrap text-xs text-text-secondary font-sans">{pickedTemplate.body}</pre>
                  </div>
                  <TemplateVariablesPanel template={pickedTemplate} values={variableOverrides} onChange={setVariableOverrides}
                    mappings={variableMappings} onMappingsChange={setVariableMappings} showContactHints />
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(pickedTemplate.headerType ?? 'NONE') && (
                    <div className="space-y-2 border-t border-border pt-4">
                      <h3 className="text-sm font-semibold text-text-primary">Campaign attachment</h3>
                      <p className="text-xs text-text-muted">Use the template media or choose a new attachment for this broadcast.</p>
                      <MediaUpload mode={pickedTemplate.headerType!.toLowerCase() as 'image' | 'video' | 'document'}
                        folder={`whatsapp/campaign/${pickedTemplate.headerType === 'IMAGE' ? 'images' : pickedTemplate.headerType === 'VIDEO' ? 'videos' : 'documents'}`}
                        value={headerOverride.imageUrl ?? headerOverride.videoUrl ?? headerOverride.documentUrl ?? pickedTemplate.headerImageUrl ?? pickedTemplate.headerVideoUrl ?? pickedTemplate.headerDocumentUrl}
                        onChange={url => setHeaderOverride({ [pickedTemplate.headerType === 'IMAGE' ? 'imageUrl' : pickedTemplate.headerType === 'VIDEO' ? 'videoUrl' : 'documentUrl']: url })} />
                    </div>
                  )}
                </>
              )}

              {messageMode === 'custom' && (() => {
                // Pull every {{name}} placeholder out of the custom body so we can
                // surface a default-fallback input for each one.
                const matches = Array.from(customMessage.matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g));
                const names = Array.from(new Set(matches.map((m) => m[1])));
                return (
                  <>
                    <div className="rounded-lg border border-border bg-surface-alt/40 p-3">
                      <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-text-muted">Custom message</div>
                      <pre className="whitespace-pre-wrap text-xs text-text-secondary font-sans">{customMessage || '(empty)'}</pre>
                    </div>
                    {names.length === 0 ? (
                      <p className="text-xs text-text-muted">
                        No <code className="text-text-secondary">{'{{var}}'}</code> placeholders in your message — nothing to configure.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-text-muted">
                          Default values for each placeholder. Per-recipient variables override these
                          when supplied.
                        </p>
                        {names.map((n) => (
                          <div key={n}>
                            <label className="mb-1 block text-xs font-medium text-text-secondary">
                              {`{{${n}}}`}
                            </label>
                            <Input
                              value={variableOverrides[n] ?? ''}
                              onChange={(e) => setVariableOverrides({ ...variableOverrides, [n]: e.target.value })}
                              placeholder={`Default value for ${n}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {step === 'schedule' && (
            <div className="space-y-4">
              <div className="flex gap-3">
                {([
                  { v: 'send_now',  l: 'Send now' },
                  { v: 'scheduled', l: 'Send at…' },
                ] as const).map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setScheduleMode(o.v)}
                    className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                      scheduleMode === o.v
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border text-text-muted hover:border-border-strong'
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>

              {scheduleMode === 'scheduled' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-text-secondary">Schedule for</label>
                  <DateTimePicker value={scheduledAt} onChange={setScheduledAt} placeholder="Pick a date & time" />
                </div>
              )}

              <CompletionEstimate
                audience={audienceCount}
                tier={accounts.find((a) => a.accountUid === accountUid)?.messagingLimitTier}
              />
            </div>
          )}

          {step === 'review' && (
            <ReviewSection
              preflight={preflight}
              loading={running && !preflight}
            />
          )}

          {step === 'send' && (
            <div className="space-y-3">
              <div className="rounded-lg border border-success/40 bg-success-soft px-4 py-3 text-sm">
                <div className="flex items-center gap-2 font-semibold text-success">
                  <CheckCircle size={14} /> Ready to send
                </div>
                <p className="mt-1 text-xs text-text-secondary">
                  Click below to dispatch the broadcast. The system will pace, jitter, and auto-pause
                  if any breaker fires. You&apos;ll be redirected to the live progress page.
                </p>
              </div>
              <Button
                size="lg"
                iconLeft={<Send size={14} />}
                onClick={() => setShowSendConfirm(true)}
              >
                Confirm and send
              </Button>
            </div>
          )}
        </Card>

        <aside className="space-y-4 xl:sticky xl:top-5">
          <Card padded className="overflow-hidden">
            <div className="mb-4 flex items-center justify-between"><h3 className="text-sm font-semibold text-text-primary">Message preview</h3><span className="text-2xs text-text-muted">Personalised per recipient</span></div>
            {audienceMode === 'contacts' && pickedContactUids.size > 0 && (
              <Select value={previewContactUid} onChange={e => setPreviewContactUid(e.target.value)} className="mb-4" aria-label="Preview recipient">
                <option value="">Choose a recipient to preview</option>
                {contacts.filter(c => pickedContactUids.has(c.contactUid)).map(c => <option key={c.contactUid} value={c.contactUid}>{[c.firstName,c.lastName].filter(Boolean).join(' ') || c.phone}</option>)}
              </Select>
            )}
            {pickedTemplate && messageMode === 'template' ? (() => {
              const contact = contacts.find(c => c.contactUid === previewContactUid) as any;
              const values = { ...variableOverrides };
              for (const name of extractTemplateVariables(pickedTemplate)) {
                const source = variableMappings[name] ?? defaultMappingFor(name);
                const value = source === 'fullName' ? [contact?.firstName,contact?.lastName].filter(Boolean).join(' ') : contact?.[source] ?? contact?.customFields?.[source];
                if (source !== 'manual' && value) values[name] = String(value);
              }
              return <TemplatePreview body={pickedTemplate.body} headerType={pickedTemplate.headerType} headerText={pickedTemplate.headerText}
                headerImageUrl={headerOverride.imageUrl ?? pickedTemplate.headerImageUrl} headerVideoUrl={headerOverride.videoUrl ?? pickedTemplate.headerVideoUrl}
                headerDocumentUrl={headerOverride.documentUrl ?? pickedTemplate.headerDocumentUrl} headerLocation={pickedTemplate.headerLocation}
                footer={pickedTemplate.footer} buttons={pickedTemplate.buttons} cards={pickedTemplate.cards} samples={values} />;
            })() : messageMode === 'custom' ? <div className="rounded-2xl bg-[#e9f2eb] p-5"><div className="rounded-xl rounded-tl-none bg-white p-4 text-sm whitespace-pre-wrap text-gray-800">{customMessage || 'Your message will appear here.'}</div></div> : <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center text-sm text-text-muted">Choose an approved template to preview your message.</div>}
          </Card>
          <div className="rounded-2xl border border-warning/20 bg-warning-soft/30 p-4 text-xs leading-relaxed text-text-secondary"><div className="mb-2 flex items-center gap-2 font-semibold text-text-primary"><ShieldCheck size={16} className="text-warning" />Protect your WhatsApp number</div>Only message people who agreed to hear from your business. High report or block rates can reduce your limits, pause templates, or restrict your number. Automatic pacing cannot prevent a ban.</div>
        </aside>
        </div>
        {/* Footer nav */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" iconLeft={<ArrowLeft size={14} />} onClick={handleBack} disabled={STEPS[0].key === step}>
            Back
          </Button>
          {step !== 'send' && (
            <Button
              iconRight={<ArrowRight size={14} />}
              onClick={handleNext}
              loading={running}
              disabled={!isStepValid(step)}
            >
              {step === 'review' ? 'Continue to send' : 'Next'}
            </Button>
          )}
        </div>
      </div>

      {/* Send confirmation */}
      <Modal open={showSendConfirm} onClose={() => setShowSendConfirm(false)} title="Confirm and send broadcast">
        <div className="space-y-3 px-6 py-5">
          <p className="text-sm text-text-primary">
            <strong>{audienceCount.toLocaleString()}</strong> recipient{audienceCount === 1 ? '' : 's'} ·{' '}
            {messageMode === 'template'
              ? <>template <span className="font-mono">{pickedTemplate?.name}</span></>
              : <>custom message ({customMessage.length} chars)</>}
          </p>
          <ul className="ml-4 list-disc space-y-1 text-xs text-text-muted">
            <li>System enforces tier limits + opt-out filtering + per-recipient cooldown.</li>
            <li>Auto-pauses if failure rate {'>'} 15%, block-rate proxy {'>'} 2%, or quality drops to RED.</li>
            <li>Pacing uses ±30% jitter and a 5-in-flight cap — no bursts.</li>
            <li>You can pause / resume / cancel at any time from the progress page.</li>
          </ul>
          <div className="-mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-border px-6 pt-4 pb-4">
            <Button variant="ghost" onClick={() => setShowSendConfirm(false)}>Back</Button>
            <Button onClick={handleStart} loading={submitting}>Send</Button>
          </div>
        </div>
      </Modal>

      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onPick={(asset) => setCustomMedia({ url: asset.url, type: asset.kind, filename: asset.filename })}
      />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CompletionEstimate({ audience, tier }: { audience: number; tier?: string }) {
  if (!tier || audience === 0) return null;
  const friendly = `Recipients are paced across your WhatsApp campaigns using ${tier} and CRM warm-up limits. Larger audiences remain queued as rolling capacity becomes available; delivery time depends on account health and Meta responses.`;
  return (
    <div className="flex items-start gap-2 rounded-lg bg-info-soft px-3 py-2 text-xs text-info">
      <Info size={14} className="mt-0.5 shrink-0" />
      <span>{friendly}</span>
    </div>
  );
}

function ReviewSection({ preflight, loading }: { preflight: PreflightResult | null; loading: boolean }) {
  if (loading || !preflight) {
    return <div className="py-8 text-center text-sm text-text-muted">Running preflight…</div>;
  }
  const { verdict, checks, audience } = preflight;
  return (
    <div className="space-y-4">
      {/* Verdict banner */}
      {verdict === 'BLOCK' && (
        <div className="flex items-start gap-2.5 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2.5 text-sm">
          <XCircle size={16} className="mt-0.5 text-danger" />
          <div>
            <div className="font-semibold text-danger">Cannot send</div>
            <p className="mt-0.5 text-xs text-text-secondary">
              At least one BLOCK-severity check failed. Fix the listed issues and re-run preflight.
            </p>
          </div>
        </div>
      )}
      {verdict === 'PASS_WITH_WARNINGS' && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5 text-sm">
          <AlertTriangle size={16} className="mt-0.5 text-warning" />
          <div>
            <div className="font-semibold text-warning">Sending allowed — review warnings</div>
            <p className="mt-0.5 text-xs text-text-secondary">
              No blockers, but some checks raised concerns. Read each before continuing.
            </p>
          </div>
        </div>
      )}
      {verdict === 'PASS' && (
        <div className="flex items-start gap-2.5 rounded-lg border border-success/40 bg-success-soft px-3 py-2.5 text-sm">
          <CheckCircle size={16} className="mt-0.5 text-success" />
          <div>
            <div className="font-semibold text-success">Ready to send</div>
            <p className="mt-0.5 text-xs text-text-secondary">All preflight checks passed.</p>
          </div>
        </div>
      )}

      {/* Audience hygiene rollup */}
      <div className="rounded-lg border border-border bg-surface-alt/40 px-3 py-2.5">
        <div className="text-xs font-semibold text-text-primary">
          {audience.totalRecipients.toLocaleString()} will be sent
        </div>
        <div className="mt-0.5 text-2xs text-text-muted">
          Cold {audience.cold} · Warm {audience.warm} · Skipped on cooldown {audience.skippedCooldown} · Cold-deferred {audience.skippedColdDefer}
        </div>
      </div>

      {/* Per-check rows */}
      <div className="space-y-1.5">
        {checks.map((c) => {
          const tone =
            c.passed
              ? 'border-success/30 bg-success-soft/40'
              : c.severity === 'BLOCK'
                ? 'border-danger/30 bg-danger-soft/40'
                : c.severity === 'WARN'
                  ? 'border-warning/30 bg-warning-soft/40'
                  : 'border-info/30 bg-info-soft/40';
          return (
            <div key={c.id} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${tone}`}>
              {c.passed ? (
                <CheckCircle size={14} className="mt-0.5 shrink-0 text-success" />
              ) : c.severity === 'BLOCK' ? (
                <XCircle size={14} className="mt-0.5 shrink-0 text-danger" />
              ) : c.severity === 'WARN' ? (
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              ) : (
                <Info size={14} className="mt-0.5 shrink-0 text-info" />
              )}
              <div className="text-xs">
                <div className="font-semibold text-text-primary">{c.id.replace(/_/g, ' ').replace(/^./, value => value.toUpperCase())}</div>
                <div className="text-text-secondary">{c.message}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
